import { CONSTANTS } from './constants';
import { clamp, gaussian, linspace, mean, mulberry32, rms, trapezoid } from './numerics';
import type {
  AppState,
  FieldComponent,
  FieldMode,
  FrequencyBand,
  PlotSeries,
  ScientificFrame,
  ScientificObservables
} from './types';

const { speedOfLight: C, planck: H, reducedPlanck: HBAR, boltzmann: KB, vacuumPermittivity: EPS0, pi: PI } = CONSTANTS;
const TWO_PI = 2 * PI;

export const thermalOccupation = (frequencyHz: number, temperatureK: number): number => {
  if (temperatureK <= 0 || frequencyHz <= 0) return 0;
  const exponent = H * frequencyHz / (KB * temperatureK);
  if (exponent > 700) return 0;
  if (Math.abs(exponent) < 1e-5) return 1 / Math.expm1(exponent);
  return 1 / Math.expm1(exponent);
};

/** Total electromagnetic energy density per hertz, including zero-point energy. */
export const spectralEnergyDensity = (frequencyHz: number, temperatureK: number): number => {
  if (frequencyHz <= 0) return 0;
  const occupation = thermalOccupation(frequencyHz, temperatureK);
  const modesPerVolumePerHz = 8 * PI * frequencyHz * frequencyHz / (C ** 3);
  return modesPerVolumePerHz * H * frequencyHz * (occupation + 0.5);
};

/** One Cartesian component of an isotropic free-space electric field. */
export const componentElectricSpectralVariance = (frequencyHz: number, temperatureK: number): number =>
  spectralEnergyDensity(frequencyHz, temperatureK) / (3 * EPS0);

export const squeezingParameter = (squeezingDb: number): number => Math.max(0, squeezingDb) * Math.log(10) / 20;
export const squeezedMinimumVarianceFactor = (squeezingDb: number): number => Math.exp(-2 * squeezingParameter(squeezingDb));
export const squeezedEnergyFactor = (squeezingDb: number): number => Math.cosh(2 * squeezingParameter(squeezingDb));

const selectedStateSpectralVariance = (frequencyHz: number, state: Readonly<AppState>): number => {
  const componentFactor = state.scientific.fieldComponent === 'magnitude' ? 3 : 1;
  const base = componentElectricSpectralVariance(frequencyHz, state.scientific.temperature) * componentFactor;
  if (state.scientific.state === 'squeezed') return base * squeezedEnergyFactor(state.scientific.squeezingDb);
  return base;
};

const selectedStateEnergyDensity = (frequencyHz: number, state: Readonly<AppState>): number => {
  const base = spectralEnergyDensity(frequencyHz, state.scientific.temperature);
  if (state.scientific.state === 'squeezed') return base * squeezedEnergyFactor(state.scientific.squeezingDb);
  return base;
};

export const resolveFrequencyBand = (state: Readonly<AppState>): FrequencyBand => {
  const centerHz = 10 ** state.scientific.frequencyLog10;
  const requestedBandwidth = 10 ** state.scientific.detectorBandwidthLog10;
  const bandwidthHz = Math.max(1, Math.min(requestedBandwidth, centerHz * 1.98));
  return {
    centerHz,
    bandwidthHz,
    lowerHz: Math.max(1, centerHz - bandwidthHz / 2),
    upperHz: centerHz + bandwidthHz / 2,
    shape: state.scientific.bandShape
  };
};

const filterWeight = (frequencyHz: number, band: FrequencyBand): number => {
  if (band.shape === 'rectangular') return frequencyHz >= band.lowerHz && frequencyHz <= band.upperHz ? 1 : 0;
  const sigma = band.bandwidthHz / 2.354820045;
  const z = (frequencyHz - band.centerHz) / Math.max(Number.MIN_VALUE, sigma);
  return Math.exp(-0.5 * z * z);
};

const integrationGrid = (band: FrequencyBand, count: number): number[] => {
  if (band.shape === 'rectangular') return linspace(band.lowerHz, band.upperHz, count);
  const sigma = band.bandwidthHz / 2.354820045;
  return linspace(Math.max(1, band.centerHz - 4 * sigma), band.centerHz + 4 * sigma, count);
};

const stableParallelAngularFactor = (q: number): number => {
  const aq = Math.abs(q);
  if (aq < 1e-3) {
    const q2 = q * q;
    return 1 - q2 / 10 + q2 * q2 / 280 - q2 * q2 * q2 / 15120;
  }
  return 3 * (Math.sin(q) - q * Math.cos(q)) / (q ** 3);
};

const stablePerpendicularAngularFactor = (q: number): number => {
  const aq = Math.abs(q);
  if (aq < 1e-3) {
    const q2 = q * q;
    return 1 - q2 / 5 + 3 * q2 * q2 / 280 - q2 * q2 * q2 / 3780;
  }
  return 3 * (q * q * Math.sin(q) + q * Math.cos(q) - Math.sin(q)) / (2 * q ** 3);
};

export const transverseAngularFactor = (q: number, component: FieldComponent): number => {
  if (component === 'x') return stableParallelAngularFactor(q);
  if (component === 'y' || component === 'z') return stablePerpendicularAngularFactor(q);
  return (stableParallelAngularFactor(q) + 2 * stablePerpendicularAngularFactor(q)) / 3;
};

const spectrumIntegral = (state: Readonly<AppState>, band: FrequencyBand, resolution: number): {
  frequencies: number[];
  selectedVariance: number[];
  vacuumVariance: number[];
  energyDensity: number;
  componentVariance: number;
  occupationMean: number;
} => {
  const frequencies = integrationGrid(band, resolution);
  const weights = frequencies.map((frequency) => filterWeight(frequency, band));
  const selectedVariance = frequencies.map((frequency, index) => selectedStateSpectralVariance(frequency, state) * (weights[index] ?? 0));
  const vacuumVariance = frequencies.map((frequency, index) => componentElectricSpectralVariance(frequency, 0) * (weights[index] ?? 0));
  const energy = frequencies.map((frequency, index) => selectedStateEnergyDensity(frequency, state) * (weights[index] ?? 0));
  const occupations = frequencies.map((frequency, index) => thermalOccupation(frequency, state.scientific.temperature) * (weights[index] ?? 0));
  const normalization = trapezoid(frequencies, weights);
  return {
    frequencies,
    selectedVariance,
    vacuumVariance,
    energyDensity: trapezoid(frequencies, energy),
    componentVariance: trapezoid(frequencies, selectedVariance),
    occupationMean: normalization > 0 ? trapezoid(frequencies, occupations) / normalization : 0
  };
};

const calculateCorrelation = (
  frequencies: readonly number[],
  spectralVariance: readonly number[],
  separationMeters: number,
  delaySeconds: number,
  component: FieldComponent
): number => {
  const values = frequencies.map((frequency, index) => {
    const q = TWO_PI * frequency * separationMeters / C;
    return (spectralVariance[index] ?? 0) * transverseAngularFactor(q, component) * Math.cos(TWO_PI * frequency * delaySeconds);
  });
  return trapezoid(frequencies, values);
};

const createFieldModes = (
  state: Readonly<AppState>,
  band: FrequencyBand,
  componentVariance: number,
  modeCount: number
): FieldMode[] => {
  const random = mulberry32(state.scientific.randomSeed);
  const modes: FieldMode[] = [];
  const frequencies = integrationGrid(band, modeCount);
  const spectral = frequencies.map((frequency) => selectedStateSpectralVariance(frequency, state) * filterWeight(frequency, band));
  const df = frequencies.length > 1 ? ((frequencies.at(-1) ?? band.upperHz) - (frequencies[0] ?? band.lowerHz)) / (frequencies.length - 1) : band.bandwidthHz;
  const componentIndex = state.scientific.fieldComponent === 'y' ? 1 : state.scientific.fieldComponent === 'z' ? 2 : 0;
  for (let index = 0; index < frequencies.length; index += 1) {
    const z = random() * 2 - 1;
    const azimuth = TWO_PI * random();
    const radial = Math.sqrt(Math.max(0, 1 - z * z));
    const direction: [number, number, number] = [radial * Math.cos(azimuth), radial * Math.sin(azimuth), z];
    const directionComponent = direction[componentIndex] ?? 0;
    const transverseWeight = state.scientific.fieldComponent === 'magnitude'
      ? 1
      : Math.sqrt(Math.max(0, (1 - directionComponent * directionComponent) / (2 / 3)));
    const binVariance = Math.max(0, (spectral[index] ?? 0) * df);
    modes.push({
      frequencyHz: frequencies[index] ?? band.centerHz,
      direction,
      amplitudeVPerM: Math.sqrt(2 * binVariance) * transverseWeight,
      phaseRad: TWO_PI * random()
    });
  }
  const expectedVariance = 0.5 * modes.reduce((sum, mode) => sum + mode.amplitudeVPerM ** 2, 0);
  const scale = expectedVariance > 0 ? Math.sqrt(componentVariance / expectedVariance) : 1;
  modes.forEach((mode) => { mode.amplitudeVPerM *= scale; });
  return modes;
};

export const evaluateFieldModes = (
  modes: readonly FieldMode[],
  xMeters: number,
  yMeters: number,
  zMeters: number,
  timeSeconds: number
): number => modes.reduce((sum, mode) => {
  const k = TWO_PI * mode.frequencyHz / C;
  const spatialPhase = k * (mode.direction[0] * xMeters + mode.direction[1] * yMeters + mode.direction[2] * zMeters);
  return sum + mode.amplitudeVPerM * Math.cos(spatialPhase - TWO_PI * mode.frequencyHz * timeSeconds + mode.phaseRad);
}, 0);

const createFieldSlice = (
  modes: readonly FieldMode[],
  state: Readonly<AppState>,
  physicalTimeSeconds: number,
  targetRms: number
): number[][] => {
  const size = clamp(Math.round(state.scientific.numericalResolution / 1.5), 24, 64);
  const extent = 10 ** state.scientific.spatialLog10;
  const values = Array.from({ length: size }, (_, row) => Array.from({ length: size }, (_, column) => {
    const x = ((column / Math.max(1, size - 1)) - 0.5) * extent;
    const y = ((row / Math.max(1, size - 1)) - 0.5) * extent;
    return evaluateFieldModes(modes, x, y, 0, physicalTimeSeconds);
  }));
  const flat = values.flat();
  const actualRms = rms(flat);
  const scale = actualRms > 0 ? targetRms / actualRms : 1;
  return values.map((row) => row.map((value) => value * scale));
};

const createQuadrature = (state: Readonly<AppState>, random: () => number): Array<{ x: number; y: number }> => {
  const occupation = thermalOccupation(10 ** state.scientific.frequencyLog10, state.scientific.temperature);
  const baseVariance = occupation + 0.5;
  let varianceX = baseVariance;
  let varianceP = baseVariance;
  let meanX = 0;
  if (state.scientific.state === 'squeezed') {
    const r = squeezingParameter(state.scientific.squeezingDb);
    varianceX *= Math.exp(-2 * r);
    varianceP *= Math.exp(2 * r);
  } else if (state.scientific.state === 'coherent') {
    meanX = Math.sqrt(2);
  }
  const theta = state.scientific.squeezingAngle * PI / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return Array.from({ length: 720 }, () => {
    const x0 = gaussian(random) * Math.sqrt(varianceX) + meanX;
    const p0 = gaussian(random) * Math.sqrt(varianceP);
    return { x: x0 * cos - p0 * sin, y: x0 * sin + p0 * cos };
  });
};

const createDetectorSeries = (
  observables: ScientificObservables,
  state: Readonly<AppState>,
  random: () => number
): PlotSeries[] => {
  const count = 128;
  const values = Array.from({ length: count }, (_, index) => ({
    x: index,
    y: gaussian(random) * observables.detectorRmsVPerM
  }));
  return [{ name: 'Ideal band-limited field samples', values, xUnit: 'sample', yUnit: 'V/m' }];
};

const casimirObservables = (state: Readonly<AppState>): Pick<ScientificObservables, 'casimirEnergyPerAreaJPerM2' | 'casimirPressurePa'> => {
  if (state.scientific.boundaryGeometry !== 'plates') return {};
  const separation = 10 ** state.scientific.boundarySeparationLog10;
  return {
    casimirEnergyPerAreaJPerM2: -(PI ** 2) * HBAR * C / (720 * separation ** 3),
    casimirPressurePa: -(PI ** 2) * HBAR * C / (240 * separation ** 4)
  };
};

export const calculateScientificFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const band = resolveFrequencyBand(state);
  const requestedResolution = clamp(Math.round(state.scientific.numericalResolution * 4), 96, 512);
  const fine = spectrumIntegral(state, band, requestedResolution);
  const coarse = spectrumIntegral(state, band, Math.max(48, Math.floor(requestedResolution / 2)));
  const integrationError = Math.abs(fine.componentVariance - coarse.componentVariance) / Math.max(fine.componentVariance, Number.MIN_VALUE);
  const selectedFieldRms = Math.sqrt(Math.max(0, fine.componentVariance));
  const electricComponentRms = state.scientific.fieldComponent === 'magnitude' ? selectedFieldRms / Math.sqrt(3) : selectedFieldRms;
  const electricMagnitudeRms = state.scientific.fieldComponent === 'magnitude' ? selectedFieldRms : selectedFieldRms * Math.sqrt(3);
  const physicalTimeSeconds = animationTimeSeconds * 10 ** state.scientific.timeLog10;
  const separationMeters = state.scientific.probeSeparation * 1e-3;
  const delaySeconds = state.scientific.probeDelay * 1e-12;
  const correlation = calculateCorrelation(fine.frequencies, fine.selectedVariance, separationMeters, delaySeconds, state.scientific.fieldComponent);
  const zeroCorrelation = calculateCorrelation(fine.frequencies, fine.selectedVariance, 0, 0, state.scientific.fieldComponent);
  const correlationCoefficient = zeroCorrelation > 0 ? clamp(correlation / zeroCorrelation, -1, 1) : 0;
  const detectorRms = selectedFieldRms * Math.sqrt(clamp(state.scientific.detectorEfficiency, 0, 1));
  const effectiveSamples = Math.max(1, (10 ** state.scientific.integrationTimeLog10) * band.bandwidthHz * 2);
  const coherenceTime = 1 / Math.max(band.bandwidthHz, Number.MIN_VALUE);
  const observables: ScientificObservables = {
    electricComponentRmsVPerM: electricComponentRms,
    electricMagnitudeRmsVPerM: electricMagnitudeRms,
    magneticRmsTesla: electricMagnitudeRms / C,
    energyDensityJPerM3: fine.energyDensity,
    meanPhotonOccupation: fine.occupationMean,
    correlationV2PerM2: correlation,
    correlationCoefficient,
    detectorRmsVPerM: detectorRms,
    detectorStandardErrorVPerM: detectorRms / Math.sqrt(effectiveSamples),
    coherenceTimeSeconds: coherenceTime,
    coherenceLengthMeters: C * coherenceTime,
    ...casimirObservables(state)
  };

  const modeCount = clamp(Math.round(state.scientific.numericalResolution), 24, 96);
  const fieldModes = createFieldModes(state, band, fine.componentVariance, modeCount);
  const fieldSlice = createFieldSlice(fieldModes, state, physicalTimeSeconds, selectedFieldRms);
  const random = mulberry32(state.scientific.randomSeed + 17);
  const quadrature = createQuadrature(state, random);
  const delayExtentSeconds = Math.min(10e-12, Math.max(5 * coherenceTime, 2e-12));
  const delays = linspace(-delayExtentSeconds, delayExtentSeconds, 121);
  const normalizedCorrelationValues = delays.map((delay) => ({
    x: delay * 1e12,
    y: zeroCorrelation > 0
      ? calculateCorrelation(fine.frequencies, fine.selectedVariance, separationMeters, delay, state.scientific.fieldComponent) / zeroCorrelation
      : 0
  }));
  const spectrum: PlotSeries[] = [
    {
      name: 'Vacuum baseline',
      values: fine.frequencies.map((frequency, index) => ({ x: frequency, y: Math.log10(Math.max(fine.vacuumVariance[index] ?? 0, 1e-300)) })),
      xUnit: 'Hz',
      yUnit: 'log10(V² m⁻² Hz⁻¹)'
    },
    {
      name: 'Selected state',
      values: fine.frequencies.map((frequency, index) => ({ x: frequency, y: Math.log10(Math.max(fine.selectedVariance[index] ?? 0, 1e-300)) })),
      xUnit: 'Hz',
      yUnit: 'log10(V² m⁻² Hz⁻¹)'
    }
  ];
  const detectorSeries = createDetectorSeries(observables, state, random);
  const phase = ((TWO_PI * band.centerHz * physicalTimeSeconds) % TWO_PI + TWO_PI) % TWO_PI;
  const displayAmplitude = clamp(0.65 + Math.log10(1 + selectedFieldRms * 20) * 0.8, 0.45, 3.8);
  const correlationNormalizationError = zeroCorrelation > 0 ? Math.abs(zeroCorrelation - fine.componentVariance) / fine.componentVariance : 0;
  const qualified = state.scientific.state === 'squeezed' || state.scientific.state === 'coherent';
  const sourceIds = new Set<string>(['codata-2022', 'milonni-1994', 'lindel-2022']);
  if (state.scientific.boundaryGeometry === 'plates') sourceIds.add('casimir-1948');
  if (state.scientific.state === 'squeezed') sourceIds.add('walls-1983');
  if (state.preset.startsWith('2019 ')) sourceIds.add('benea-chelmus-2019');
  if (state.preset.startsWith('2022 ')) sourceIds.add('lindel-2022');
  if (state.preset.startsWith('Direct ')) sourceIds.add('riek-2015');

  return {
    warning: qualified
      ? 'Qualified Phase 7 research-release candidate extension: the free-space spectrum is quantitative; squeezed/coherent behavior uses a declared covariance-envelope model rather than a complete apparatus reconstruction.'
      : 'Validated Phase 7 research-release candidate Modern baseline: band-limited free-space electromagnetic vacuum/thermal model in SI units. Material dispersion and detector transfer functions are not included.',
    modelId: 'free-space-band-limited-em-v1',
    modelVersion: '1.1.0',
    adapterId: 'modern',
    seed: state.scientific.randomSeed,
    time: animationTimeSeconds,
    physicalTimeSeconds,
    phase,
    displayAmplitude,
    frequencyBand: band,
    observables,
    adapterMetrics: [
      { label: 'Band centre', value: band.centerHz, unit: 'Hz' },
      { label: 'Band width', value: band.bandwidthHz, unit: 'Hz' },
      { label: 'Coherence time', value: observables.coherenceTimeSeconds, unit: 's' },
      { label: 'Coherence length', value: observables.coherenceLengthMeters, unit: 'm' }
    ],
    equations: [
      {
        id: 'free-space-spectrum',
        label: 'Band-limited free-space electromagnetic spectrum',
        html: '<i>S</i><sub>Eᵢ</sub>(ν,T) = <span class="fraction"><span>8πhν³</span><span>3ε₀c³</span></span>[n̄(ν,T)+½]',
        plain: 'S_Ei(nu,T)=8*pi*h*nu^3[nbar(nu,T)+1/2]/(3*epsilon_0*c^3)',
        sourceIds: ['milonni-1994', 'codata-2022']
      },
      {
        id: 'free-space-correlation',
        label: 'Transverse two-probe correlation',
        html: '<i>C</i><sub>i</sub>(r,τ) = ∫dν <i>S</i><sub>Eᵢ</sub>(ν)<i>A</i><sub>i</sub>(2πνr/c)cos(2πντ)',
        plain: 'C_i(r,tau)=integral dnu S_Ei(nu) A_i(2*pi*nu*r/c) cos(2*pi*nu*tau)',
        sourceIds: ['lindel-2022']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Spectrum',
      spectrumSubtitle: 'log10 S_E(nu)',
      quadratureTitle: 'Quadratures',
      correlationTitle: 'Correlation',
      detectorTitle: 'Detector Output',
      sliceTitle: 'Field Snapshot'
    },
    renderingMapping: 'A seeded plane-wave realization maps the authoritative band-limited covariance to visible points, vectors, and a field slice. Visual gain changes presentation only.',
    validation: {
      status: qualified ? 'qualified-extension' : 'validated-baseline',
      scope: 'Band-limited isotropic free-space electromagnetic field with ideal detector filter and transverse two-point correlation tensor.',
      relativeIntegrationError: integrationError,
      correlationNormalizationError,
      deterministic: true,
      assumptions: [
        'Linear, homogeneous, isotropic free space.',
        'Detector response represented by an ideal rectangular or Gaussian frequency filter.',
        'Probe separation lies along the x axis; the selected field component controls the transverse tensor factor.',
        'No material dispersion, crystal phase matching, finite probe volume, or electronic transfer function.',
        state.scientific.state === 'squeezed' ? 'Squeezed state represented by a single covariance-envelope parameter across the selected band.' : 'Stationary vacuum or thermal state.'
      ],
      sourceIds: Array.from(sourceIds)
    },
    spectrum,
    quadrature,
    correlationSeries: [{ name: 'Normalized transverse correlation', values: normalizedCorrelationValues, xUnit: 'ps', yUnit: 'dimensionless' }],
    detectorSeries,
    fieldSlice,
    fieldModes
  };
};
